import React, { forwardRef, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { cn } from '../../../lib/cn';
import { Icon } from '../../Icon';
import { MOD_LABEL, isModifierHeld } from './platform';

/**
 * SearchPanel, the global search typeahead.
 *
 * CSS: `desktop/system.css` `.dsk-searchpanel` and its six sub-parts,
 * `__scroll`, `__group`, `__head`, `__glyph`, `__foot`, `__hint`, plus
 * `.dsk-search`, `.dsk-kbd`, `.dsk-listing-row` (results are the SAME card
 * model as the marketplace list view), `.dsk-searchpanel mark` and
 * `.dsk-empty--sm`.
 * Behaviour: ports BEHAVIOUR #6, and reuses BEHAVIOUR #4's platform detection
 * through `platform.js` rather than re-detecting it.
 *
 * What #6 does and this keeps:
 *   opens on CLICK anywhere on the box, on FOCUS, and on Cmd+K / Ctrl+K  ·
 *   grouped results with a count per group  ·  Arrow Up/Down move the
 *   highlight, Enter opens the highlighted row, Escape closes and RESTORES
 *   FOCUS to the input  ·  the pointer and the keyboard agree on what is
 *   highlighted  ·  the panel is `position: fixed` against the box, so no
 *   wrapper can clip it  ·  a resting state with trending and recent searches,
 *   so the box is never a dead end before anything is typed  ·  matched
 *   substrings are marked, which is what proves it is really filtering.
 *
 * React differences, deliberate:
 *   - Matches are marked by splitting into React nodes, not by building an
 *     HTML string. #6 had to escape by hand precisely because it used
 *     `innerHTML`; there is nothing to escape here.
 *   - `aria-activedescendant` still points at the highlighted option, so the
 *     input keeps focus while the listbox highlight moves, which is what makes
 *     Escape-restores-focus meaningful.
 *   - The panel renders its own `.dsk-search` box. #6 attached to whichever
 *     boxes carried a `.dsk-kbd`; in React the field and its panel are one
 *     component so they cannot desync.
 */

/* Every token must appear somewhere in the record, so "charizard ex" narrows
   rather than widening the way a plain OR would. Same rule as #6. */
export function matchesTokens(haystack, tokens) {
  const lower = String(haystack).toLowerCase();
  return tokens.every((token) => lower.indexOf(token) > -1);
}

/**
 * Split text on the matched tokens and wrap the hits in <mark>.
 * `.dsk-searchpanel mark` is the tinted highlight in the layer.
 */
export function markMatches(text, tokens) {
  const source = String(text == null ? '' : text);
  if (!tokens.length) return source;

  const pattern = tokens.map((token) => token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  const re = new RegExp(`(${pattern})`, 'ig');
  const parts = source.split(re);

  return parts.map((part, index) =>
    index % 2 === 1 ? <mark key={`m-${index}`}>{part}</mark> : part,
  );
}

/**
 * SearchPanel.
 *
 * @param {object[]} groups
 *   [{ id, label, count, items: [{ id, kicker, title, meta, trailing, glyph,
 *      href, onSelect }] }]
 *   `glyph` is a Phosphor component for the rows that have no card art (sets
 *   and stores), which take a `.dsk-searchpanel__glyph` in the same footprint
 *   as the tile so every row stays on one baseline grid.
 */
export const SearchPanel = forwardRef(function SearchPanel(
  {
    query = '',
    onQueryChange,
    groups = [],
    onSelect,
    onSubmit,
    placeholder = 'Search cards, sets and stores',
    fieldLabel = 'Search',
    shortcutKey = 'K',
    showShortcut = true,
    emptyTitle,
    emptyText = 'Check the spelling, or search by set name or card number.',
    enterHint,
    /* 'inline'  the search box on the page, its panel hanging under it.
       'palette' a centred command palette: no box on the page at all, the
                 field lives INSIDE the panel, a scrim sits behind it, and the
                 only way in is the shortcut. Same options, same keyboard, same
                 chrome — the difference is where it is and what summons it. */
    variant = 'inline',
    onOpenChange,
    className,
    panelClassName,
    ...rest
  },
  ref,
) {
  const isPalette = variant === 'palette';
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const base = useId();

  const [open, setOpen] = useState(false);
  const [at, setAt] = useState(-1);
  const [position, setPosition] = useState(null);

  const tokens = useMemo(
    () => String(query || '').trim().toLowerCase().split(/\s+/).filter(Boolean),
    [query],
  );

  /* One flat option list across every group: the arrow keys walk the panel, not
     a group. Indices are what `aria-activedescendant` points at. */
  const options = useMemo(() => {
    const flat = [];
    groups.forEach((groupEntry) => {
      (groupEntry.items || []).forEach((item) => flat.push(item));
    });
    return flat;
  }, [groups]);

  const optionId = (index) => `${base}-option-${index}`;

  const place = useCallback(() => {
    if (isPalette) {
      /* Centred horizontally, but NOT vertically: a palette pinned to the
         middle of a tall window drifts down as the window grows, and the
         thing you look at first is the field, not the box's centroid. A
         fixed 15vh keeps the field in the same place on every screen and
         leaves the list room to grow downward. */
      const width = Math.min(640, window.innerWidth - 32);
      setPosition({
        width,
        left: Math.round((window.innerWidth - width) / 2),
        top: Math.round(Math.min(160, window.innerHeight * 0.15)),
      });
      return;
    }

    const box = boxRef.current;
    if (!box) return;
    const rect = box.getBoundingClientRect();
    /* The same clamping #6 uses: at least 560px wide, never wider than the
       viewport less a 16px gutter, never hanging off either edge. */
    const width = Math.min(Math.max(rect.width, 560), window.innerWidth - 32);
    setPosition({
      width,
      left: Math.min(Math.max(16, rect.left), window.innerWidth - width - 16),
      top: rect.bottom + 8,
    });
  }, [isPalette]);

  const close = useCallback(
    (refocus) => {
      setOpen(false);
      setAt(-1);
      /* Closing must never strand focus on a dead panel — except in palette
         mode, where the field IS the panel and refocusing it would reopen
         what was just dismissed. */
      if (refocus && !isPalette && inputRef.current) inputRef.current.focus();
      if (onOpenChange) onOpenChange(false);
    },
    [isPalette, onOpenChange],
  );

  const openPanel = useCallback(() => {
    place();
    setOpen(true);
    if (onOpenChange) onOpenChange(true);
  }, [place, onOpenChange]);

  useEffect(() => {
    if (!open) return undefined;
    place();

    const onOutside = (event) => {
      if (panelRef.current && panelRef.current.contains(event.target)) return;
      if (boxRef.current && boxRef.current.contains(event.target)) return;
      close(false);
    };

    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    document.addEventListener('mousedown', onOutside, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      document.removeEventListener('mousedown', onOutside, true);
    };
  }, [open, place, close]);

  /* Cmd+K on Mac, Ctrl+K elsewhere. The platform is detected once in
     platform.js and nothing here re-detects it. */
  useEffect(() => {
    const onKeyDown = (event) => {
      if (isModifierHeld(event) && event.key.toLowerCase() === shortcutKey.toLowerCase()) {
        event.preventDefault();
        if (isPalette && open) {
          // The shortcut that opens it closes it. A palette you cannot
          // dismiss with the keys that summoned it is a trap.
          close(false);
          return;
        }
        openPanel();
        // In palette mode the field lives inside a panel that is `hidden`
        // until this render commits, and a hidden element cannot take focus.
        // The effect below does it once the panel is really there.
        if (!isPalette && inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
        return;
      }
      if (event.key === 'Escape' && open) close(true);
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, openPanel, close, shortcutKey, isPalette]);

  // Focus the palette's field the moment it is actually visible.
  useEffect(() => {
    if (!isPalette || !open || !inputRef.current) return;
    inputRef.current.focus();
    inputRef.current.select();
  }, [isPalette, open]);

  const highlight = (next) => {
    if (!options.length) return;
    setAt(((next % options.length) + options.length) % options.length);
  };

  /* Keep the highlighted row in view without moving focus off the input. */
  useEffect(() => {
    if (at < 0 || !panelRef.current) return;
    const node = panelRef.current.querySelector(`[data-option-index="${at}"]`);
    if (node) node.scrollIntoView({ block: 'nearest' });
  }, [at]);

  const choose = (item, index) => {
    close(true);
    if (item && item.onSelect) item.onSelect(item, index);
    else if (onSelect) onSelect(item, index);
  };

  const onFieldKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) openPanel();
      highlight(at + 1);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) openPanel();
      highlight(at - 1);
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      close(true);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (open && at > -1 && options[at]) choose(options[at], at);
      else if (onSubmit) onSubmit(query);
      close(false);
    }
  };

  let runningIndex = -1;
  const hasResults = options.length > 0;

  /* The field itself, identical wherever it is mounted: on the page in inline
     mode, at the top of the panel in palette mode. One element, so the two
     variants cannot drift into two different search boxes. */
  const field = (
    <>
      <Icon as={MagnifyingGlass} />
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        value={query}
        placeholder={placeholder}
        autoComplete="off"
        aria-expanded={open}
        aria-controls={`${base}-listbox`}
        aria-autocomplete="list"
        aria-activedescendant={open && at > -1 ? optionId(at) : undefined}
        onChange={(event) => {
          if (!open) openPanel();
          if (onQueryChange) onQueryChange(event.target.value);
        }}
        onFocus={isPalette ? undefined : openPanel}
        onKeyDown={onFieldKeyDown}
      />
      <span className="dsk-sr-only">{fieldLabel}</span>
      {showShortcut && !isPalette ? (
        <span className="dsk-kbd" aria-hidden="true">{`${MOD_LABEL}${shortcutKey}`}</span>
      ) : null}
    </>
  );

  const panel = (
    <div
      ref={(node) => {
        panelRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      className={cn('dsk-searchpanel', open && 'is-open', panelClassName)}
      hidden={!open}
      role="presentation"
      /* Geometry only. Surface, border, shadow, radius and z-index are all in
         `.dsk-searchpanel`. */
      style={
        position
          ? { width: `${position.width}px`, left: `${position.left}px`, top: `${position.top}px` }
          : undefined
      }
    >
      {isPalette ? <div className="dsa-palette__field dsk-search">{field}</div> : null}

      <div
        className="dsk-searchpanel__scroll"
        id={`${base}-listbox`}
        role="listbox"
        aria-label="Search suggestions"
      >
        {hasResults ? (
          groups.map((groupEntry) => (
            <div className="dsk-searchpanel__group" key={groupEntry.id || groupEntry.label}>
              <div className="dsk-searchpanel__head">
                {groupEntry.label}
                {groupEntry.count != null ? <span className="n">{groupEntry.count}</span> : null}
              </div>
              {(groupEntry.items || []).map((item) => {
                runningIndex += 1;
                const index = runningIndex;
                const active = index === at;
                const GlyphIcon = item.glyph;

                return (
                  // eslint-disable-next-line jsx-a11y/anchor-is-valid
                  <a
                    key={item.id != null ? item.id : `${groupEntry.label}-${index}`}
                    id={optionId(index)}
                    data-option-index={index}
                    className={cn('dsk-listing-row', active && 'is-active')}
                    role="option"
                    aria-selected={active}
                    href={item.href}
                    onMouseMove={() => {
                      if (index !== at) setAt(index);
                    }}
                    onClick={(event) => {
                      if (!item.href) event.preventDefault();
                      choose(item, index);
                    }}
                  >
                    {GlyphIcon ? (
                      <span className="dsk-searchpanel__glyph">
                        <Icon as={GlyphIcon} size="sm" />
                      </span>
                    ) : item.thumb ? (
                      /* Real art when there is any. The empty tile below is a
                         placeholder, and a panel of placeholders makes every
                         card look the same at exactly the moment you are
                         trying to tell them apart. */
                      <img className="dsk-listing-row__tile" src={item.thumb} alt="" />
                    ) : (
                      <span className="dsk-listing-row__tile" />
                    )}

                    <span className="dsk-listing-row__main">
                      {item.kicker ? (
                        <span className="dsk-listing-row__kicker">
                          {markMatches(item.kicker, tokens)}
                        </span>
                      ) : null}
                      <span className="dsk-listing-row__title">
                        {markMatches(item.title, tokens)}
                      </span>
                      {item.meta ? <span className="dsk-listing-row__meta">{item.meta}</span> : null}
                    </span>

                    {item.trailing ? (
                      <span className="dsk-listing-row__prices">{item.trailing}</span>
                    ) : null}
                  </a>
                );
              })}
            </div>
          ))
        ) : (
          <div className="dsk-empty dsk-empty--sm">
            <span className="dsk-empty__icon">
              <Icon as={MagnifyingGlass} />
            </span>
            <p className="dsk-empty__title">
              {emptyTitle || (
                <>
                  No matches for <span className="dsk-empty__query">{query}</span>
                </>
              )}
            </p>
            <p className="dsk-empty__text">{emptyText}</p>
          </div>
        )}
      </div>

      <div className="dsk-searchpanel__foot">
        <span className="dsk-searchpanel__hint">
          {enterHint || (
            <>
              <span className="dsk-kbd">Enter</span>
              {query ? (
                <>
                  {' to see all results for '}
                  <strong>{query}</strong>
                </>
              ) : (
                ' to open the full search page'
              )}
            </>
          )}
        </span>
        <span className="dsk-searchpanel__hint">
          <span className="dsk-kbd">&#8593;</span>
          <span className="dsk-kbd">&#8595;</span> to move
          <span className="dsk-kbd">Esc</span> to close
        </span>
      </div>
    </div>
  );

  if (isPalette) {
    /* No control on the page at all. A palette is summoned, never sited —
       putting a box on the page for it would be a second search field beside
       the nav's, which is the duplication the shortcut exists to avoid. */
    return typeof document !== 'undefined'
      ? createPortal(
          <>
            {open ? (
              <div
                className="dsa-palette__scrim"
                role="presentation"
                onMouseDown={() => close(false)}
              />
            ) : null}
            {panel}
          </>,
          document.body,
        )
      : null;
  }

  return (
    <>
      {/* Click anywhere on the box opens it, which was #6's whole complaint:
          the search looked like a control and behaved like a label. */}
      <label
        ref={boxRef}
        className={cn('dsk-search', className)}
        onClick={openPanel}
        {...rest}
      >
        {field}
      </label>

      {typeof document !== 'undefined' ? createPortal(panel, document.body) : null}
    </>
  );
});

export default SearchPanel;
